<template>
    <CompassModal
        v-model:visible="showModal"
        :title="$t.__('document.CustomFieldsSettingsModal.title')"
        :size="CompassEnums.ModalSize.SMALL"
        class="custom-fields-settings-modal"
        :coloredIconProps="coloredIconProps"
        :primaryAction="primaryAction"
        :cancelButtonProps="cancelButtonProps"
        @closed="emit('close')"
    >
        <p class="custom-fields-settings-modal__description typographies-paragraph-medium-regular">
            <TranslationText :parts="description" />
        </p>
        <div v-if="isLoading" class="custom-fields-settings-modal__loading">
            <CompassSkeleton />
            <CompassSkeleton size="x-large" />
            <CompassSkeleton />
            <CompassSkeleton size="x-large" />
            <CompassSkeleton />
            <CompassSkeleton size="x-large" />
        </div>
        <CompassEmptyState
            v-else-if="!customFields.values.length"
            illustrationPath="/ressources/logged/img/illustrations/empty-custom-fields.svg"
            :title="$t.__('document.CustomFieldsSettingsModal.empty_state.title')"
            :caption="$t.__('document.CustomFieldsSettingsModal.empty_state.description')"
        />
        <div v-else class="custom-fields-settings-modal__list">
            <CompassAccordion
                v-for="group in filteredGroupedCustomFields"
                :key="getAccordionKey(group.id)"
                :title="group.name"
                :open="isAccordionOpen(group.id, group.openByDefault)"
                :variant="CompassEnums.AccordionVariantType.H3"
                @toggle="(isOpen: boolean) => handleAccordionToggle(group.id, isOpen)"
            >
                <CustomFieldList
                    :customFields="group.customFields"
                    shouldDisplayInfoOnPdf
                    @update:customField="handleUpdateCustomField"
                />
            </CompassAccordion>
            <CompassAccordion
                v-if="ungroupedCustomFields?.customFields.length"
                :key="getAccordionKey(0, 'ungrouped')"
                :title="$t.__('common.labels.customFields.title_accordion_cf_ungrouped')"
                :variant="CompassEnums.AccordionVariantType.H3"
                :open="isAccordionOpen(0, ungroupedCustomFields.openByDefault)"
                @toggle="(isOpen: boolean) => handleAccordionToggle(0, isOpen)"
            >
                <CustomFieldList
                    :customFields="ungroupedCustomFields.customFields"
                    shouldDisplayInfoOnPdf
                    @update:customField="handleUpdateCustomField"
                />
            </CompassAccordion>
        </div>
    </CompassModal>
</template>
<script setup lang="ts">
import { Translator } from 'js/shims-vue';
import { computed, ComputedRef, inject, onMounted, Ref, ref, toRef } from 'vue';
import { useTranslationLinkParser } from 'composables/useTranslationLinkParser';
import { useDocumentFormStore } from 'js/vue/stores/document/documentFormStore';
import {
    getFilteredCustomFields,
    getUngroupedCustomFields,
} from 'Helpers/customFields/customFieldsHelpers';
import CustomFieldList from 'vcomponents/common/customFields/CustomFieldList.vue';
import TranslationText from 'vcomponents/common/TranslationText.vue';
import {
    CustomFieldType,
    type CustomField,
    type CustomFieldGroup,
} from 'js/types/customFields/customFields.type';
import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';
import { ERROR_CATEGORIES } from 'Config/authenticated/dictionary/docForm/docFormEnums';

// Injected
const $t = inject('$t') as Translator;

// Use
const { parseTranslation } = useTranslationLinkParser();

// Store
const documentFormStore = useDocumentFormStore();
const { customFields, validationErrors } = storeToRefs(documentFormStore);
const temporary = toRef(documentFormStore, 'temporary') as Ref<Record<string, unknown>>;

// Emits
const emit = defineEmits(['close']);

// Refs
const showModal = ref(false);
const isSaving = ref(false);
const isLoading = ref(false);
const openGroupIds = ref<Set<number>>(new Set());
const accordionKeyMap = ref<Map<number, number>>(new Map());

// Consts
const cancelButtonProps = {
    label: $t.__('common.actions.cancel'),
    action: () => {
        customFields.value.values = JSON.parse(JSON.stringify(temporary.value.customFields));
        emit('close');
    },
};
const coloredIconProps = {
    icon: 'custom-fields',
    color: CompassEnums.ColoredIconColorType.BRAND,
};
const description = parseTranslation($t.__('document.CustomFieldsSettingsModal.description'));

// Computed
const ungroupedCustomFields: ComputedRef<CustomFieldGroup | null> = computed(() => {
    return getUngroupedCustomFields(customFields.value.values);
});
const filteredGroupedCustomFields: ComputedRef<CustomFieldGroup[]> = computed(() => {
    return getFilteredCustomFields(customFields.value.values);
});

const getAccordionKey = (groupId: number, prefix?: string): string => {
    const key = prefix ?? String(groupId);
    const version = accordionKeyMap.value.get(groupId);
    return version !== undefined ? `${key}-${version}` : key;
};

const isAccordionOpen = (groupId: number, openByDefault: boolean): boolean => {
    return openGroupIds.value.has(groupId) || openByDefault;
};

// Methods
const handleAccordionToggle = (groupId: number, isOpen: boolean): void => {
    if (isOpen) {
        openGroupIds.value.add(groupId);
    } else {
        openGroupIds.value.delete(groupId);
    }
};

const handleSave = async (): Promise<void> => {
    isSaving.value = true;
    if (!customFields.value.values.length) {
        emit('close');
        return;
    }
    if (await documentFormStore.validateCustomFields()) {
        // so that the cancel button can reset to the new values after "saving"
        temporary.value.customFields = JSON.parse(JSON.stringify(customFields.value.values));
        validationErrors.value = validationErrors.value.filter(
            (error: { type: string }) => error.type !== ERROR_CATEGORIES.CUSTOMFIELDS,
        );
        emit('close');
    } else {
        const errorGroupIds = (customFields.value.values as CustomField[])
            .filter((cf) => cf.error)
            .map((cf) => cf.customfield_group.id);
        const closedErrorGroupIds = errorGroupIds.filter((id) => !openGroupIds.value.has(id));
        for (const id of closedErrorGroupIds) {
            accordionKeyMap.value.set(id, (accordionKeyMap.value.get(id) ?? 0) + 1);
            openGroupIds.value.add(id);
        }
    }
    isSaving.value = false;
};

const primaryAction: ComputedRef<{ label: string; loading: boolean; action: () => Promise<void> }> =
    computed(() => ({
        label: $t.__('common.actions.save'),
        loading: isSaving.value,
        action: handleSave,
    }));

const handleUpdateCustomField = (id: number, value: unknown): void => {
    const values = customFields.value.values as CustomField[];
    if (!values?.length) {
        return;
    }
    const customField = values.find((field) => field.id === id);
    if (!customField) {
        return;
    }
    if (!isEqual(customField.value, value)) {
        customField.error = null;
    }
    customField.value = value as CustomField['value'];
    if (
        [
            CustomFieldType.COMPANY_INDIVIDUAL,
            CustomFieldType.CONTACT,
            CustomFieldType.STAFF,
            CustomFieldType.ITEM,
        ].includes(customField.type)
    ) {
        const items = value as Array<{ id: number; label: string }>;
        (customField as { _embed: unknown })._embed = {
            value_details: items.map((item) => ({ id: item.id, name: item.label })),
        };
        customField.value = items.map(({ id }) => id);
    }
};

// Lifecycle
onMounted((): void => {
    showModal.value = true;
    // store a temporary copy of custom fields values to be able to reset them when clicking on cancel after saving
    temporary.value.customFields = JSON.parse(JSON.stringify(customFields.value.values));
});
</script>
<style lang="scss" scoped>
.custom-fields-settings-modal {
    &__description {
        margin: 0 0 var(--spacing-3);
    }

    &__loading {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);

        .compass-skeleton:nth-child(odd) {
            width: 15%;
        }

        .compass-skeleton:has(.compass-skeleton__line--x-large):not(:last-child) {
            margin-bottom: var(--spacing-4);
        }
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }
}
</style>

<style>
#compass-dropdown-container,
#compass-popover-container {
    .custom-fields__select,
    .custom-fields__datepicker,
    .compass-autocomplete__dropdown,
    .compass-timepicker__popover {
        z-index: 1001;
    }
}
</style>
